import * as Sentry from "@sentry/react";
import type { StripeError } from "@stripe/stripe-js";
import { capitalize, compact, includes, reject } from "lodash/fp";
import { DateTime, Duration } from "luxon";

import ROUTES from "@app/common/routes";
import type {
  BillingCadence,
  BillingCallToAction,
  BillingCurrentPlan,
  BillingCustomer,
  BillingPlan,
  BillingScheduledPlan,
  BillingSubscription,
  CardPaymentMethod,
  CheckoutBillingInfo,
  Invoice,
  PaymentMethod,
  PreviewInvoice,
  StripeBillingInfo,
  User,
} from "@app/common/types";
import { pluralizePhrase } from "@app/common/utils";
import { planIsUnknown } from "@app/common/utils/billingUtils";
import type { ApiEnterpriseWorkspaceUser } from "@app/common/workspaces/types";
import Config from "@app/config";
import {
  camelizeKeys,
  parseUTCDateTimeFromString,
  titleize,
} from "@app/utils/utils";

import {
  LABEL_FOR_PAYMENT_METHOD_TYPE,
  LABEL_FOR_PLAN,
  TRUNCATED_LABEL_FOR_PLAN,
} from "./constants";

const PLANS_IN_ASC_ORDER: BillingPlan[] = [
  "free",
  "zoom_bundle_trial",
  "pro",
  "enterprise",
  "enterprise_plus",
];

type StripeErrorEvent = { elementType: string; error: StripeError };

export const handleLoadError = (event: StripeErrorEvent) => {
  Sentry.addBreadcrumb({
    category: "stripe",
    data: { elementType: event.elementType, error: event.error },
    level: "error",
    message: `Stripe ${event.elementType} load error: ${event.error.message}`,
  });

  Sentry.captureException(
    new Error(`Stripe ${event.elementType} load error: ${event.error.message}`)
  );
};

const CADENCE_IN_ASC_ORDER: BillingCadence[] = ["monthly", "annual"];

const FREE_PLANS: BillingPlan[] = ["free", "zoom_bundle_trial"];

export const isPlanUpgrade = (plan: BillingPlan, nextPlan: BillingPlan) =>
  plan !== nextPlan &&
  PLANS_IN_ASC_ORDER.indexOf(nextPlan) > PLANS_IN_ASC_ORDER.indexOf(plan);

type PlanDowngradeArg = Pick<BillingCurrentPlan, "type" | "billingCadence">;

export const isPlanDowngrade = (
  currentPlan: PlanDowngradeArg,
  scheduledPlan?: PlanDowngradeArg
) => {
  if (!scheduledPlan) return true;

  if (currentPlan.type === scheduledPlan.type) {
    if (!currentPlan.billingCadence || !scheduledPlan.billingCadence) {
      return false;
    }

    return (
      CADENCE_IN_ASC_ORDER.indexOf(currentPlan.billingCadence) >
      CADENCE_IN_ASC_ORDER.indexOf(scheduledPlan.billingCadence)
    );
  }

  return !isPlanUpgrade(currentPlan.type, scheduledPlan.type);
};

export const getPlanLabel = (plan?: BillingPlan) => {
  return LABEL_FOR_PLAN[plan ?? "free"];
};

export const getTruncatedPlanLabel = (plan?: BillingPlan) => {
  const currentPlan: BillingPlan = plan ?? "free";
  const truncatedLabel = TRUNCATED_LABEL_FOR_PLAN[currentPlan];
  return truncatedLabel ?? getPlanLabel(currentPlan);
};

export const isFreePlan = (
  plan: BillingPlan,
  exact = false
): plan is (typeof FREE_PLANS)[number] => {
  if (exact) return plan === "free";
  return FREE_PLANS.includes(plan);
};

export const isProPlan = (plan: BillingPlan) => {
  return plan === "pro";
};

export const isZoomPlan = (plan: BillingPlan) => plan === "zoom_bundle_trial";

export const isPaidPlan = (plan: BillingPlan) => !isFreePlan(plan);

export const planSupportsVideoRecording = (plan: BillingPlan) =>
  PLANS_IN_ASC_ORDER.indexOf(plan) >= PLANS_IN_ASC_ORDER.indexOf("enterprise");

export const subscriptionWillCancel = (subscription: BillingSubscription) => {
  return subscription.cancelAtPeriodEnd || subscription.cancelAt != null;
};

export const isCard = (
  paymentMethod: PaymentMethod
): paymentMethod is CardPaymentMethod => paymentMethod.type === "card";

export const getPaymentMethodDescription = (paymentMethod: PaymentMethod) => {
  if (isCard(paymentMethod)) {
    return `${titleize(paymentMethod.card.brand)} ending in ${
      paymentMethod.card.last4
    }`;
  }

  return (
    LABEL_FOR_PAYMENT_METHOD_TYPE[paymentMethod.type] ??
    titleize(paymentMethod.type)
  );
};

export const getPlanPriceByPriceId = (
  priceId: BillingSubscription["stripePriceId"]
) => {
  if (!priceId) return;

  if (includes(priceId, Config.billing.zeroBalancePriceIds)) {
    return 0;
  }

  const price = Config.billing.prices.byPriceId[priceId];
  if (!price) {
    Sentry.captureMessage(`No price found for priceId ${priceId}`);
    return;
  }
  return price;
};

export const getPlanPriceByPlanAndCadence = ({
  cadence = "monthly",
  plan,
  useLegacyPrices,
  forEntirePeriod = false,
}: {
  cadence?: BillingCadence;
  plan: BillingPlan;
  useLegacyPrices: boolean;
  /** Only relevant for annual plans. If true, will return the price per year instead of price per month */
  forEntirePeriod?: boolean;
}) => {
  if (isFreePlan(plan)) return 0;

  const multiplier = forEntirePeriod && cadence === "annual" ? 12 : 1;

  const prices = useLegacyPrices
    ? Config.billing.prices.legacyByPlan
    : Config.billing.prices.byPlan;

  return prices[plan][cadence] * multiplier;
};

export const getBillingCallToAction = (
  currentPlan: BillingCurrentPlan,
  isTrial: boolean
): BillingCallToAction | undefined => {
  if (planIsUnknown(currentPlan.type)) return;

  if (!isFreePlan(currentPlan.type) && !isTrial) {
    return {
      title: "Manage",
      url: ROUTES.BILLING,
    };
  }

  return {
    title: "Upgrade",
    url: ROUTES.UPGRADE_ACCOUNT,
  };
};

export const getAnnualSavings = (useLegacyPrices: boolean) => {
  // savings should be the same regardless of which plan we're using, but just default to the Pro
  // annual savings
  const monthlySingleLicensePrice = getPlanPriceByPlanAndCadence({
    plan: "pro",
    cadence: "monthly",
    useLegacyPrices,
  });

  const annualMonthlySingleLicensePrice = getPlanPriceByPlanAndCadence({
    plan: "pro",
    cadence: "annual",
    useLegacyPrices,
  });

  return Math.round(
    (1 - annualMonthlySingleLicensePrice / monthlySingleLicensePrice) * 100
  );
};

export const getCurrentPeriodEndDescription = ({
  currentPlan,
  isTrial,
  plansInitialLoading,
  scheduledPlan,
  subscription,
}: {
  currentPlan: BillingCurrentPlan;
  isTrial: boolean;
  plansInitialLoading: boolean;
  scheduledPlan?: BillingScheduledPlan;
  subscription: BillingSubscription;
}): string | null => {
  if (
    plansInitialLoading ||
    !subscription.currentPeriodEnd ||
    isFreePlan(currentPlan.type, true)
  ) {
    return null;
  }

  const formattedPlanChangeDate = DateTime.fromISO(
    subscription.cancelAt ?? subscription.currentPeriodEnd
  ).toLocaleString({
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const trialWillCancel =
    isTrial && (!scheduledPlan || scheduledPlan.type === currentPlan.type);

  const willDowngradeAtPeriodEnd =
    trialWillCancel ||
    Boolean(subscription.cancelAt) ||
    subscription.cancelAtPeriodEnd;

  if (willDowngradeAtPeriodEnd) {
    return `Your plan will automatically be downgraded to ${LABEL_FOR_PLAN.free} on ${formattedPlanChangeDate}`;
  }

  if (
    scheduledPlan &&
    (subscription.inTrialPeriod || !isSamePlan(currentPlan, scheduledPlan))
  ) {
    if (scheduledPlan.type === "zoom_bundle_trial") {
      return `Your plan will automatically be downgraded to the ${scheduledPlan.label} free trial on ${formattedPlanChangeDate}`;
    }

    const cadenceAdverb = getCadenceAdverb(
      scheduledPlan.billingCadence ?? "monthly"
    );
    return `Your plan will automatically switch to ${scheduledPlan.label} (billed ${cadenceAdverb}) on ${formattedPlanChangeDate}`;
  }

  return `Next invoice ${formattedPlanChangeDate}`;
};

export const buildEmailMailtoLink = (
  workspaceOwner: ApiEnterpriseWorkspaceUser,
  user: User
) => {
  const emailSubject = "Request to Upgrade Read";
  const emailBody = `Hi ${
    workspaceOwner?.firstName ?? "there"
  }, I would like to request an upgrade to our Read Workspace. Premium features like Workspace analytics, advanced Meeting Report metrics, transcriptions, and playback would be beneficial for our team.
  \n Here are additional details: 
  \n Upgrade Plan: https://app.read.ai/analytics/settings/billing
  \n Compare Read Plans: https://www.read.ai/plans-pricing
  \n Thanks, 
  \n ${user.fullName}`;

  return `mailto:${
    workspaceOwner.email
  }?subject=${emailSubject}&body=${encodeURIComponent(emailBody)}`;
};

export const getPaymentStatus = (invoice: Invoice) => {
  if (invoice.paid) {
    return "Paid";
  }

  if (invoice.finalizationError) {
    return "Error";
  }

  if (invoice.attempted && invoice.nextPaymentAttempt == null) {
    return "Failed";
  }

  if (invoice.status === "open") {
    return "Pending";
  }

  return capitalize(invoice.status);
};

export const getCadenceAdverb = (cadence: BillingCadence) =>
  cadence === "monthly" ? "monthly" : "annually";

export const isSamePlan = (
  currentPlan: Pick<BillingCurrentPlan, "type" | "billingCadence">,
  scheduledPlan: Pick<BillingScheduledPlan, "type" | "billingCadence">
) => {
  return (
    currentPlan.type === scheduledPlan.type &&
    currentPlan.billingCadence === scheduledPlan.billingCadence
  );
};

export const getFormattedAddress = (
  details?:
    | PaymentMethod["billingDetails"]
    | StripeBillingInfo
    | BillingCustomer
    | CheckoutBillingInfo
    | null
): string[] | undefined => {
  if (!details) return;

  const camelizedDetails = camelizeKeys(details);

  // if there's a value for "line1", we know that this user has put in a full billing address
  if (!camelizedDetails.address?.line1) return;

  const company = "company" in details ? details.company : undefined;
  const { name, address } = camelizedDetails;

  const lastLine = compact([
    address.city,
    compact([address.state, address.postalCode]).join(" "),
    address.country,
  ]).join(", ");

  return compact([company, name, address.line1, address.line2, lastLine]);
};

export const getRenewalDescription = ({
  dueDate,
  inTrialPeriod,
  plan,
  renewalDate,
}: {
  dueDate?: DateTime;
  inTrialPeriod: boolean;
  plan: BillingPlan;
  renewalDate: DateTime;
}) => {
  const formattedRenewalDate = renewalDate.toLocaleString({
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const basicRenewalDescription = `Auto renews ${formattedRenewalDate}`;

  if (inTrialPeriod) {
    return `Your ${getPlanLabel(
      plan
    )} plan will start immediately after payment. ${basicRenewalDescription}.`;
  }

  if (dueDate) {
    return `Your plan will automatically change to ${getPlanLabel(
      plan
    )} next billing period. ${basicRenewalDescription}.`;
  }

  return basicRenewalDescription;
};

export const getProratedPriceBreakdown = (
  subscription: BillingSubscription
) => {
  if (!subscription.stripePriceId || !subscription.currentPeriodEnd) return {};

  const sub =
    subscription.inTrialPeriod && subscription.scheduledUpdate
      ? subscription.scheduledUpdate
      : subscription;

  const monthlyPrice = getPlanPriceByPriceId(sub.stripePriceId) ?? 0;

  const totalPricePerLicense =
    monthlyPrice * (sub.billingCadence === "annual" ? 12 : 1);

  let timeRemaining = parseUTCDateTimeFromString(
    subscription.currentPeriodEnd
  ).diffNow();

  if (subscription.inTrialPeriod) {
    timeRemaining = Duration.fromObject({
      year: sub.billingCadence === "annual" ? 1 : 0,
      month: sub.billingCadence === "monthly" ? 1 : 0,
    });
  }

  const monthsRemaining = timeRemaining.as("months");

  if (monthsRemaining >= 1) {
    return {
      pricePerLicense: totalPricePerLicense,
      remainingTime: pluralizePhrase(
        Math.round(monthsRemaining),
        "remaining month"
      ),
    };
  }

  const daysRemaining = Math.round(timeRemaining.as("days"));

  return {
    pricePerLicense: totalPricePerLicense,
    remainingTime: pluralizePhrase(daysRemaining, "remaining day"),
  };
};

export const getTaxRateFromInvoice = (invoice?: PreviewInvoice) => {
  if (!invoice?.tax) return;

  const rate = invoice.tax / invoice.subtotal;
  return (rate * 100).toFixed(2);
};

export const getDueDateFromInvoice = (invoice?: PreviewInvoice) => {
  if (!invoice) return;

  // preview invoice will have a period start if the payment is deferred
  // (e.g., user is currently on a free trial)
  if (invoice.periodStart) {
    return invoice.periodStart.toLocaleString({
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  return "today";
};

export const filterInvoices = (invoices: Invoice[]) => {
  return reject(
    ({ stripePriceId, description }) =>
      includes(stripePriceId, Config.billing.zeroBalancePriceIds) ||
      includes("Trial period", description),
    invoices
  );
};
